import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import Axios from 'axios';
import type { AxiosRequestData, HttpProgressUpdate, IHttpClient } from './Types';

const DEFAULT_AXIOS_HEADERS = {
    'X-Requested-With': 'XMLHttpRequest',
    Accept: 'application/json',
    'Content-Type': 'application/json',
};

class HttpClient implements IHttpClient {
    private baseUrl: string;

    private axios: AxiosInstance;

    constructor(baseUrl: string, defaultHeaders: Record<string, string> = {}) {
        this.baseUrl = baseUrl;

        this.axios = Axios.create({
            headers: {
                ...DEFAULT_AXIOS_HEADERS,
                ...defaultHeaders,
            },
        });
    }

    Axios(): AxiosInstance {
        return this.axios;
    }

    Headers() {
        return this.axios.defaults.headers;
    }

    BaseUrl(): string {
        return this.baseUrl;
    }

    addHeader(name: string, value: string) {
        this.axios.defaults.headers = {
            ...this.axios.defaults.headers,
            [name]: value,
        };
    }

    removeHeader(name: string) {
        const headers: Record<string, unknown> = this.axios.defaults.headers;

        delete headers[name];
    }

    /**
     * An upload is multipart unless the caller names another content type. Leaving the header out
     * is not enough: axios merges the instance's `application/json` default in, and from 0.27 a
     * FormData under a JSON content type is serialised to JSON (it was sent as `"null"`). Under
     * multipart, the XHR adapter removes the header so the browser adds the boundary. The other
     * instance headers (Accept, X-Impersonating, ...) are merged in by axios itself.
     */
    private static fileHeaders(additionalHeaders?: Record<string, string>): Record<string, string> {
        return { 'Content-Type': 'multipart/form-data', ...(additionalHeaders ?? {}) };
    }

    get<T>(url: string, config?: AxiosRequestConfig): AxiosRequestData<T> {
        const controller = new AbortController();
        return {
            controller,
            request: this.axios.get<T>(this.baseUrl + url, {
                withCredentials: true,
                signal: controller.signal,
                ...config,
            }),
        };
    }

    put<T>(url: string, data: any, config?: AxiosRequestConfig): AxiosRequestData<T> {
        const controller = new AbortController();
        return {
            controller,
            request: this.axios.put<T>(this.baseUrl + url, data, {
                withCredentials: true,
                signal: controller.signal,
                ...config,
            }),
        };
    }

    post<T>(url: string, data: any, config?: AxiosRequestConfig): AxiosRequestData<T> {
        const controller = new AbortController();
        return {
            controller,
            request: this.axios.post(this.baseUrl + url, data, {
                withCredentials: true,
                signal: controller.signal,
                ...config,
            }),
        };
    }

    patch<T>(url: string, data: any, config?: AxiosRequestConfig): AxiosRequestData<T> {
        const controller = new AbortController();
        return {
            controller,
            request: this.axios.patch(this.baseUrl + url, data, {
                withCredentials: true,
                signal: controller.signal,
                ...config,
            }),
        };
    }

    postFile(
        url: string,
        data: FormData,
        onProgressUpdate?: HttpProgressUpdate,
        additionalHeaders?: any,
        config?: AxiosRequestConfig,
    ): AxiosRequestData<any> {
        const headers = HttpClient.fileHeaders(additionalHeaders);

        const controller = new AbortController();

        return {
            controller,
            request: this.axios.post<any>(this.baseUrl + url, data, {
                withCredentials: true,
                headers,
                maxContentLength: Infinity,
                onUploadProgress: onProgressUpdate,
                signal: controller.signal,
                ...config,
            }),
        };
    }

    putFile(
        url: string,
        data: FormData,
        onProgressUpdate?: HttpProgressUpdate,
        additionalHeaders?: any,
        config?: AxiosRequestConfig,
    ): AxiosRequestData<any> {
        const headers = HttpClient.fileHeaders(additionalHeaders);

        const controller = new AbortController();

        return {
            controller,
            request: this.axios.put<any>(this.baseUrl + url, data, {
                withCredentials: true,
                headers,
                maxContentLength: Infinity,
                onUploadProgress: onProgressUpdate,
                signal: controller.signal,
                ...config,
            }),
        };
    }

    delete<T>(url: string, data?: any, config?: AxiosRequestConfig): AxiosRequestData<T> {
        const controller = new AbortController();
        return {
            controller,
            request: this.axios.delete<T>(this.baseUrl + url, {
                withCredentials: true,
                signal: controller.signal,
                data,
                ...config,
            }),
        };
    }
}

export default HttpClient;
